/**
 * Generic Column Manager Hook
 *
 * Manages column visibility and order with URL persistence.
 * Reusable across any datatable (tasks, matters, intake, etc.).
 *
 * Architecture:
 * - visibleColumns is the single source of truth (order + visibility)
 * - displayColumns is derived from visibleColumns + allColumns
 * - URL is updated on every change (immediate, like filters)
 * - Configuration-driven via getDefaultColumns function
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  DEFAULT_COLUMN_URL_PARAM,
  parseColumnsFromUrl,
  serializeColumnsToUrl,
} from '../columnUtils';
import { TableColumn } from '../types';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export interface UseColumnManagerOptions<TData> {
  /** All available columns from the column factory */
  allColumns: TableColumn<TData>[];
  /** Function to get default columns (can be vertical-aware or static) */
  getDefaultColumns: () => string[];
  /** Columns that must always remain visible */
  requiredColumns?: string[];
  /** URL param key for column state (default: 'cols') */
  urlParamKey?: string;
  /** Optional layout version param to persist alongside column updates */
  layoutVersion?: {
    key: string;
    value: string;
  };
}

export interface UseColumnManagerReturn<TData> {
  /** Current visible column IDs in display order */
  visibleColumns: string[];
  /** Filtered and ordered columns ready for DataTable */
  displayColumns: TableColumn<TData>[];
  /** Toggle a column's visibility */
  toggleColumn: (columnId: string) => void;
  /** Reorder columns (move from one index to another) */
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  /** Set all visible columns at once (for drag-drop batch update) */
  setVisibleColumns: (columns: string[]) => void;
  /** Reset to default layout */
  resetToDefault: () => void;
  /** Check if a column is currently visible */
  isColumnVisible: (columnId: string) => boolean;
  /** Check if current layout differs from default */
  hasChanges: boolean;
  /** All columns available (for the visibility menu) */
  allColumns: TableColumn<TData>[];
  /** Default columns */
  defaultColumns: string[];
}

// ----------------------------------------------------------------------------
// Hook Implementation
// ----------------------------------------------------------------------------

export function useColumnManager<TData>({
  allColumns,
  getDefaultColumns,
  requiredColumns = [],
  urlParamKey = DEFAULT_COLUMN_URL_PARAM,
  layoutVersion,
}: UseColumnManagerOptions<TData>): UseColumnManagerReturn<TData> {
  const [searchParams, setSearchParams] = useSearchParams();

  const enforceRequiredColumns = useCallback(
    (columns: string[], referenceOrder: string[]) => {
      if (requiredColumns.length === 0) {
        return columns;
      }

      return requiredColumns.reduce((nextColumns, requiredColumn) => {
        if (nextColumns.includes(requiredColumn)) {
          return nextColumns;
        }

        const requiredIndex = referenceOrder.indexOf(requiredColumn);
        if (requiredIndex === -1) {
          return [...nextColumns, requiredColumn];
        }

        const nextReferenceColumn = referenceOrder
          .slice(requiredIndex + 1)
          .find((columnId) => nextColumns.includes(columnId));

        if (!nextReferenceColumn) {
          return [...nextColumns, requiredColumn];
        }

        const insertIndex = nextColumns.indexOf(nextReferenceColumn);
        return [
          ...nextColumns.slice(0, insertIndex),
          requiredColumn,
          ...nextColumns.slice(insertIndex),
        ];
      }, [...columns]);
    },
    [requiredColumns],
  );

  // Get default columns (memoized based on function reference)
  const defaultColumns = useMemo(
    () => {
      const baseDefaultColumns = getDefaultColumns();
      return enforceRequiredColumns(baseDefaultColumns, baseDefaultColumns);
    },
    [enforceRequiredColumns, getDefaultColumns],
  );

  // Build set of valid column IDs from allColumns for validation
  const validColumnIds = useMemo(
    () => new Set(allColumns.map((col) => col.id)),
    [allColumns],
  );

  const validRequiredColumns = useMemo(
    () => requiredColumns.filter((id) => validColumnIds.has(id)),
    [requiredColumns, validColumnIds],
  );

  // Parse visible columns from URL or use defaults
  const visibleColumns = useMemo(() => {
    const urlParam = searchParams.get(urlParamKey);
    const parsed = parseColumnsFromUrl(urlParam);

    if (!parsed) {
      return defaultColumns;
    }

    // Filter out invalid column IDs (silently ignore removed/renamed columns)
    const validParsed = parsed.filter((id) => validColumnIds.has(id));

    // Fall back to defaults if all parsed columns are invalid
    return enforceRequiredColumns(
      validParsed.length > 0 ? validParsed : defaultColumns,
      defaultColumns,
    );
  }, [
    defaultColumns,
    enforceRequiredColumns,
    searchParams,
    urlParamKey,
    validColumnIds,
  ]);

  // Compute display columns: filter and order allColumns based on visibleColumns
  const displayColumns = useMemo(() => {
    const columnMap = new Map(allColumns.map((col) => [col.id, col]));
    return visibleColumns
      .map((id) => columnMap.get(id))
      .filter((col): col is TableColumn<TData> => col !== undefined);
  }, [allColumns, visibleColumns]);

  // Update URL with new column order
  const updateColumns = useCallback(
    (newColumns: string[]) => {
      const nextColumns = enforceRequiredColumns(newColumns, defaultColumns);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const serialized = serializeColumnsToUrl(nextColumns, defaultColumns);

          if (serialized === null) {
            // Matches defaults, remove param for cleaner URL
            next.delete(urlParamKey);
          } else {
            next.set(urlParamKey, serialized);
          }

          if (layoutVersion) {
            next.set(layoutVersion.key, layoutVersion.value);
          }

          return next;
        },
        { replace: true }, // Don't create new history entry for column changes
      );
    },
    [defaultColumns, enforceRequiredColumns, layoutVersion, setSearchParams, urlParamKey],
  );

  // Toggle a column's visibility
  const toggleColumn = useCallback(
    (columnId: string) => {
      const isVisible = visibleColumns.includes(columnId);

      if (isVisible && validRequiredColumns.includes(columnId)) {
        return;
      }

      // Prevent hiding the last visible column
      if (isVisible && visibleColumns.length === 1) {
        return;
      }

      const newColumns = isVisible
        ? visibleColumns.filter((id) => id !== columnId)
        : [...visibleColumns, columnId]; // Add to end when showing
      updateColumns(newColumns);
    },
    [updateColumns, validRequiredColumns, visibleColumns],
  );

  // Reorder columns by moving from one index to another
  const reorderColumns = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      const newColumns = [...visibleColumns];
      const [moved] = newColumns.splice(fromIndex, 1);
      newColumns.splice(toIndex, 0, moved);
      updateColumns(newColumns);
    },
    [visibleColumns, updateColumns],
  );

  // Set all visible columns at once (for batch operations)
  const setVisibleColumns = useCallback(
    (columns: string[]) => {
      updateColumns(columns);
    },
    [updateColumns],
  );

  // Reset to default layout
  const resetToDefault = useCallback(() => {
    updateColumns(defaultColumns);
  }, [updateColumns, defaultColumns]);

  // Check if a specific column is visible
  const isColumnVisible = useCallback(
    (columnId: string) => visibleColumns.includes(columnId),
    [visibleColumns],
  );

  // Check if current layout differs from default
  const hasChanges = useMemo(() => {
    if (visibleColumns.length !== defaultColumns.length) return true;
    return visibleColumns.some((id, idx) => id !== defaultColumns[idx]);
  }, [visibleColumns, defaultColumns]);

  return {
    visibleColumns,
    displayColumns,
    toggleColumn,
    reorderColumns,
    setVisibleColumns,
    resetToDefault,
    isColumnVisible,
    hasChanges,
    allColumns,
    defaultColumns,
  };
}
